import { jsx, jsxs } from '@emotion/react/jsx-runtime';
import styled from '@emotion/styled';
import { useTheme, css } from '@emotion/react';
import { LoadSpinner } from '@monorepo/load-spinner';
import { lightTheme } from '@monorepo/theme-provider';
import { jsx as jsx$1 } from 'react/jsx-runtime';
import { useRef, useState, useEffect } from 'react';

// using css prop because tag is dynamically set as <a> or <button>
const buttonStyles = (options) => {
    const { theme, isBackgroundLess, isBorderLess, buttonColors, disabled, fullWidth, hyperlink, large, reverse, } = options;
    const hoverColor = theme.palette.primary[1];
    const activeColor = theme.palette.primary[4];
    const fontColor = isBackgroundLess ? theme.colors.textDefault : buttonColors.font;
    return css `
        background: ${isBackgroundLess ? "none" : buttonColors.bg};
        border-color: ${buttonColors.bg};
        border-style: ${isBorderLess ? "none" : "solid"};
        color: ${fontColor};
        cursor: ${disabled ? "default" : "pointer"};
        padding: ${hyperlink ? 0 : large ? "8px 15px" : "10.5px 12.25px"};
        border-width: 2px;
        display: inline-block;
        outline: none;
        position: relative;
        line-height: 1;
        ${fullWidth && "width: 100%;"}

        // &:lang(ja-jp),
        // &:lang(ko-kr),
        // &:lang(zh-cn),
        // &:lang(zh-tw) {
        //     padding: ${hyperlink ? 0 : large ? "8px 15px" : "7px 12px 8px"};
        // }

        ${hyperlink
        ? `
                font-size: inherit;
            `
        : `
                font-size: ${large ? 18 : 14}px;
                font-weight: bold;
                text-decoration: none;
                `}
        ${disabled &&
        theme.name === "dark" &&
        isBackgroundLess &&
        `
                border-color: #333;
                background: none;
                color: #666;   
            `}
        ${disabled &&
        theme.name === "dark" &&
        !isBackgroundLess &&
        `
                border-color: #333;
                background: #333;
                color: #666;   
            `}
        ${!disabled &&
        `
            &:hover {
                border-color: ${isBackgroundLess ? theme.colors.textDefault : hoverColor};
                background: ${isBackgroundLess ? "none" : hoverColor};
                color: ${fontColor};                
                span {
                    left: ${reverse ? -10 : 10}px;
                }
                
                svg > path {
                    stroke: ${theme.colors.textDefault};
                }
            }

            &:active {
                border-color: ${activeColor};
                background: ${isBackgroundLess ? "none" : activeColor};
            }
        `}
        > span {
            // this needs to be inline for the no-break space below (&#xFEFF;)
            // to work. Since we can't use reverse flex-direction, the
            // chevron and load spinner must be explicitly rendered around the
            // button label according to the "reverse" prop below
            display: inline;
        }
        ${theme.mediaQueries.lg} {
            padding: ${hyperlink ? 0 : large ? "8px 15px" : "12px 14px"};
            line-height: 1;
            ${hyperlink
        ? `
                    font-size: inherit;
                `
        : `
                    font-size: ${large ? 18 : 16}px;
                    font-weight: bold;
                    text-decoration: none;
                    `}
        }
    `;
};
const ChevronContainer = styled.span `
    left: ${({ reverse }) => (reverse ? -5 : 5)}px;
    position: relative;
    transition: all 200ms ease-out;
    white-space: nowrap;

    ${({ reverse }) => reverse &&
    `
        > svg {
            transform: rotate(180deg)
        }
    `}
`;
const Chevron = ({ color, large, reverse }) => {
    // &#xFEFF; is a zero-width no-break space that prevents the chevron from
    // being wrapped to the next line on its own. It forces the last word in the
    // button label to be wrapped along with the chevron
    return (jsxs(ChevronContainer, { reverse: reverse, children: ["\uFEFF", jsx("svg", { width: 9, height: large ? 13 : 10, viewBox: "0 0 9 13", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: jsx("path", { d: "M1.625 12.25L7.375 6.5L1.625 0.75", stroke: color, strokeWidth: "3" }) })] }));
};
const ImageButtonLink = styled.a `
    background: black;
    display: inline-block;
    padding: 0 !important;

    img {
        display: block;
        margin: auto;
        max-height: 40px;
    }
`;
const Button = (props) => {
    const { children, disabled, secondary, fullWidth, hollow, hyperlink, textOnly, imageUrl, imageStyles, inlineStyles, label, large, isLoading, reverse, forceLightTheme, ...otherProps } = props;
    const theme = forceLightTheme ? lightTheme : useTheme();
    const isBackgroundLess = secondary || textOnly || hollow;
    const isBorderLess = secondary || textOnly;
    const getButtonColors = () => {
        if (disabled) {
            return {
                bg: theme.palette.grey[4],
                font: theme.palette.grey[3],
            };
        }
        return {
            bg: theme.palette.primary[0],
            font: "black",
        };
    };
    if (imageUrl) {
        return (jsx(ImageButtonLink, { ...otherProps, children: jsx("img", { src: imageUrl, style: imageStyles }) }));
    }
    // button should only be <a> when enabled. Disabled attribute has no effect on <a> tag.
    const ButtonTag = otherProps.href && !disabled ? "a" : "button";
    const buttonColors = getButtonColors();
    const buttonOptions = { theme, isBackgroundLess, isBorderLess, buttonColors, ...props };
    const renderLoadSpinner = () => isLoading && (jsx(LoadSpinner, { color: isBackgroundLess ? buttonColors.bg : buttonColors.font, inline: true, inlineRight: reverse, size: large ? "xs" : "xxs" }));
    const renderChevron = () => secondary && jsx(Chevron, { reverse: reverse, color: buttonColors.bg, large: large });
    return (jsxs(ButtonTag, { className: `button ${hyperlink ? "hyperlink" : ""}`, css: buttonStyles(buttonOptions), style: inlineStyles, disabled: disabled, role: "button", ...otherProps, children: [reverse ? renderChevron() : renderLoadSpinner(), jsxs("span", { children: [children || label, reverse ? renderLoadSpinner() : renderChevron()] })] }));
};

const Container = styled.div `
    display: flex;
    white-space: nowrap;
    flex-direction: ${(props) => (props.vertical ? "column" : "row")};
    ${(props) => (props.vertical ? "align-items" : "justify-content")}: ${(props) => {
    switch (props.align) {
        case "left":
            return "flex-start";
        case "right":
            return "flex-end";
        case "center":
            return "center";
        default:
            return null;
    }
}};

    > span {
        &:not(:first-of-type) {
            ${(props) => `${props.vertical ? "margin-top" : "margin-left"}: ${props.gap}px;`}
        }

        a,
        button {
            min-width: ${(props) => props.buttonWidth}px;
        }
    }
`;
const ButtonGroup = (props) => {
    const containerRef = useRef();
    const [normalizedWidth, setNormalizedWidth] = useState(0);
    useEffect(() => {
        // set all buttons in the group to the same width. Requires additional render
        if (props.normalizeWidths) {
            setNormalizedWidth(Object.keys(containerRef.current.children).reduce((acc, curr) => {
                const buttonWidth = containerRef.current.children[curr].children[0].clientWidth;
                return Math.max(acc, buttonWidth);
            }, 0));
        }
    }, []);
    // <a> tags need to be wrapped in div otherwise they always go full width
    return (jsx$1(Container, { ...props, buttonWidth: normalizedWidth, ref: containerRef, children: props.children.map((child, index) => (jsx$1("span", { children: child }, index))) }));
};

export { Button, ButtonGroup };
